import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

// ─────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────

const isValidUrl = (url) =>
  !!url &&
  typeof url === 'string' &&
  url.trim() !== '' &&
  url.toLowerCase() !== 'null' &&
  url.toLowerCase() !== 'undefined';

// ── File type detectors ───────────────────────────────────────────────────────

/** Extract file extension from URL (ignores query params) */
const getFileExtension = (url) => {
  if (!isValidUrl(url)) return '';
  // Strip query string and get the path part
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : '';
  } catch {
    // Fallback if URL is not fully qualified
    const match = url.split('?')[0].match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : '';
  }
};

const PDF_EXTS   = ['pdf'];
const WORD_EXTS  = ['doc', 'docx'];
const PPT_EXTS   = ['ppt', 'pptx'];
const EXCEL_EXTS = ['xls', 'xlsx'];

const isPdf    = (url) => PDF_EXTS.includes(getFileExtension(url));
const isWord   = (url) => WORD_EXTS.includes(getFileExtension(url));
const isPpt    = (url) => PPT_EXTS.includes(getFileExtension(url));
const isExcel  = (url) => EXCEL_EXTS.includes(getFileExtension(url));
const isAnyDoc = (url) => [...PDF_EXTS, ...WORD_EXTS, ...PPT_EXTS, ...EXCEL_EXTS].includes(getFileExtension(url));

// Keep isDocumentFile for backward compat (used in website/social check)
const isDocumentFile = (url) => isAnyDoc(url);

/**
 * VIEWER SELECTION BY FILE EXTENSION
 * ────────────────────────────────────
 *  .pdf           →  Google Docs Viewer     (primary) → PDF.js (fallback) → Direct
 *  .doc / .docx   →  Google Docs Viewer     (primary) → Microsoft (fallback) → Direct
 *  .ppt / .pptx   →  Microsoft Office Viewer(primary) → Google (fallback) → Direct
 *  .xls / .xlsx   →  Microsoft Office Viewer(primary) → Google (fallback) → Direct
 *  Google Drive   →  Drive /preview embed
 *  Website/social →  Direct iframe
 */
const buildViewerStrategies = (rawUrl, contentType) => {
  if (!isValidUrl(rawUrl)) return [];

  // ── Google Drive link → always drive /preview ────────────────────────────
  if (rawUrl.includes('drive.google.com')) {
    const m = rawUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (m) {
      return [{
        name: 'Google Drive',
        url: `https://drive.google.com/file/d/${m[1]}/preview`,
        timeout: 30000,
        label: '☁️ Loading via Google Drive…',
        hint: 'Google Drive preview',
      }];
    }
  }

  const isDoc = isAnyDoc(rawUrl);

  // ── Website / social media: load directly (unless it IS a document) ───────
  if ((contentType === 'website' || contentType === 'social_media') && !isDoc) {
    return [{
      name: 'Direct',
      url: rawUrl,
      timeout: 15000,
      label: '🌐 Loading website…',
      hint: 'Direct load',
    }];
  }

  const ext = getFileExtension(rawUrl);
  const strategies = [];

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  PDF (.pdf)  →  Google Docs Viewer  →  PDF.js  →  Direct Browser
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (isPdf(rawUrl)) {
    strategies.push({
      name: 'Google Docs Viewer',
      url: `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`,
      timeout: 30000,
      label: '📄 Loading PDF via Google Docs Viewer…',
      hint: 'Google Docs Viewer — PDF',
    });
    strategies.push({
      name: 'PDF.js Viewer',
      url: `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(rawUrl)}`,
      timeout: 45000,
      label: '📑 Loading PDF via PDF.js…',
      hint: 'PDF.js — no size limit (fallback)',
    });
    strategies.push({
      name: 'Direct Browser',
      url: rawUrl,
      timeout: 20000,
      label: '🌐 Opening PDF directly in browser…',
      hint: 'Native browser PDF renderer',
    });
    return strategies;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  WORD (.doc / .docx)  →  Google Docs Viewer  →  Microsoft  →  Direct
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (isWord(rawUrl)) {
    strategies.push({
      name: 'Google Docs Viewer',
      url: `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`,
      timeout: 30000,
      label: '📄 Loading Word file via Google Docs Viewer…',
      hint: 'Google Docs Viewer — Word document',
    });
    strategies.push({
      name: 'Microsoft Office Viewer',
      url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`,
      timeout: 60000,
      label: '📝 Loading Word via Microsoft Office Viewer…',
      hint: 'Microsoft Office Viewer — up to 100 MB',
    });
    strategies.push({
      name: 'Direct Browser',
      url: rawUrl,
      timeout: 20000,
      label: '🌐 Opening document directly…',
      hint: 'Native browser rendering',
    });
    return strategies;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  PPT (.ppt / .pptx)  →  Microsoft Office Viewer  →  Google  →  Direct
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (isPpt(rawUrl)) {
    strategies.push({
      name: 'Microsoft Office Viewer',
      url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`,
      timeout: 60000,
      label: '📊 Loading PPT via Microsoft Office Viewer…',
      hint: 'Microsoft Office Viewer — up to 100 MB',
    });
    strategies.push({
      name: 'Google Docs Viewer',
      url: `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`,
      timeout: 30000,
      label: '📄 Loading PPT via Google Docs Viewer…',
      hint: 'Google Docs Viewer — fallback',
    });
    strategies.push({
      name: 'Direct Browser',
      url: rawUrl,
      timeout: 20000,
      label: '🌐 Opening presentation directly…',
      hint: 'Native browser rendering',
    });
    return strategies;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  EXCEL  (.xls / .xlsx)  →  Microsoft  →  Google  →  Direct
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (isExcel(rawUrl)) {
    strategies.push({
      name: 'Microsoft Office Viewer',
      url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`,
      timeout: 60000,
      label: '📊 Loading Excel via Microsoft Office Viewer…',
      hint: 'Microsoft Office Viewer — up to 100 MB',
    });
    strategies.push({
      name: 'Google Docs Viewer',
      url: `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`,
      timeout: 30000,
      label: '📄 Loading Excel via Google Docs Viewer…',
      hint: 'Google Docs Viewer — fallback',
    });
    strategies.push({
      name: 'Direct Browser',
      url: rawUrl,
      timeout: 20000,
      label: '🌐 Opening file directly…',
      hint: 'Native browser rendering',
    });
    return strategies;
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  Unknown extension → try both viewers then direct
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  strategies.push({
    name: 'Google Docs Viewer',
    url: `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`,
    timeout: 30000,
    label: `📄 Loading .${ext || 'file'} via Google Docs Viewer…`,
    hint: 'Google Docs Viewer',
  });
  strategies.push({
    name: 'Microsoft Office Viewer',
    url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(rawUrl)}`,
    timeout: 60000,
    label: `📊 Loading .${ext || 'file'} via Microsoft Viewer…`,
    hint: 'Microsoft Office Viewer',
  });
  strategies.push({
    name: 'Direct Browser',
    url: rawUrl,
    timeout: 20000,
    label: '🌐 Opening file directly…',
    hint: 'Native browser rendering',
  });

  return strategies;
};

// Sidebar config
const CONTENT_CONFIG = {
  document: {
    label: 'Document',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
      </svg>
    ),
  },
  book: {
    label: 'Book',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
      </svg>
    ),
  },
  ppt: {
    label: 'PPT',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z"/>
        <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
      </svg>
    ),
  },
  reference_book: {
    label: 'Reference',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd"/>
      </svg>
    ),
  },
  website: {
    label: 'Website',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd"/>
      </svg>
    ),
  },
  social_media: {
    label: 'Social',
    icon: (
      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
        <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"/>
      </svg>
    ),
  },
};

const CONTENT_ORDER = [ 'document', 'book', 'ppt','reference_book', 'website'/*, 'social_media'*/];

// ─────────────────────────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────────────────────────

const CourseViewer = ({
  courseData,
  modelData,
  onBack,
  showSidebar = true,
  showBottomControls = true,
  showHeader = false,
  siblingsModals = [],   // ← same product ke baaki saare modals
  onSelectModel = null,  // ← jab user left sidebar se koi modal select kare
  productName = '',      // ← product ka naam sidebar header mein dikhane ke liye
}) => {
  const [currentPage, setCurrentPage]       = useState(1);
  const [viewMode, setViewMode]             = useState('pdf');
  const [isFullscreen, setIsFullscreen]     = useState(false);
  const [activeContent, setActiveContent]   = useState(null);

  // Viewer strategy state
  const [strategies, setStrategies]         = useState([]);     // array of {name, url}
  const [strategyIdx, setStrategyIdx]       = useState(0);      // which strategy is active
  const [iframeLoading, setIframeLoading]   = useState(false);
  const [iframeKey, setIframeKey]           = useState(0);
  const [allFailed, setAllFailed]           = useState(false);

  // Per-strategy timeout
  const timeoutRef = useRef(null);

  // ── Derived (must be before TTS so totalPages is initialized first) ────────
  const data = modelData || courseData;

  const contentUrls = Object.fromEntries(
    CONTENT_ORDER.map((k) => [k, isValidUrl(data?.[k]) ? data[k] : null])
  );

  const totalPages      = data?.totalPages || 52;
  const hasAnyContent   = CONTENT_ORDER.some((k) => contentUrls[k]);
  const currentStrategy = strategies[strategyIdx] || null;
  const rawUrl          = activeContent ? contentUrls[activeContent] : null;

  const [showMobileModules, setShowMobileModules] = useState(false); // left sidebar on mobile

  // ── TTS State ─────────────────────────────────────────────────────────────
  const [ttsStatus, setTtsStatus]           = useState('stopped'); // 'stopped' | 'playing' | 'paused' | 'reading'
  const [ttsVoices, setTtsVoices]           = useState([]);
  const [selectedVoice, setSelectedVoice]   = useState('');
  const [ttsRate, setTtsRate]               = useState(1);
  const [ttsPageTime, setTtsPageTime]       = useState(0);   // seconds elapsed on current page
  const [ttsHighlight, setTtsHighlight]     = useState('');  // full page text being read
  const [ttsWordIndex, setTtsWordIndex]     = useState(-1);  // index of word currently spoken
  const [ttsWords, setTtsWords]             = useState([]);  // words array of current page
  const [showTextPanel, setShowTextPanel]   = useState(false); // show/hide the reading panel
  const [showVoiceMenu, setShowVoiceMenu]   = useState(false);
  const textPanelRef = useRef(null);
  const activeWordRef = useRef(null);
  const [pdfPageTexts, setPdfPageTexts]     = useState([]);   // extracted text per page from real doc
  const [pdfLoading, setPdfLoading]         = useState(false);
  const [pdfError, setPdfError]             = useState('');

  // ── Flipbook state ─────────────────────────────────────────────────────────
  const [docViewMode, setDocViewMode]       = useState('pdf');   // 'pdf' | 'flipbook'
  const [flipbookUrl, setFlipbookUrl]       = useState(null);
  const [flipbookLoading, setFlipbookLoading] = useState(false);
  const [flipbookError, setFlipbookError]   = useState('');
  const ttsUtterRef   = useRef(null);
  const ttsTimerRef   = useRef(null);
  const voiceMenuRef  = useRef(null);
  const pdfDocRef     = useRef(null);   // holds loaded pdfjs doc

  // ── Load PDF.js from CDN and extract text from actual document ─────────────
  const loadPdfJs = useCallback(() => new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('PDF.js failed to load'));
    document.head.appendChild(script);
  }), []);

  // ── Load JSZip (needed for PPT/DOCX parsing) ──────────────────────────────
  const loadJSZip = useCallback(() => new Promise((resolve, reject) => {
    if (window.JSZip) { resolve(window.JSZip); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    s.onload = () => resolve(window.JSZip);
    s.onerror = () => reject(new Error('JSZip failed to load'));
    document.head.appendChild(s);
  }), []);

  // ── Extract text from PPTX (slide by slide) ───────────────────────────────
  const extractPptxText = useCallback(async (url) => {
    const JSZip = await loadJSZip();
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
    const arrayBuf = await resp.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuf);

    // Find all slide XML files in order
    const slideKeys = Object.keys(zip.files)
      .filter(k => /^ppt\/slides\/slide\d+\.xml$/.test(k))
      .sort((a, b) => {
        const na = parseInt(a.match(/\d+/)[0]);
        const nb = parseInt(b.match(/\d+/)[0]);
        return na - nb;
      });

    const texts = [];
    for (const key of slideKeys) {
      const xml = await zip.files[key].async('string');
      // Extract all <a:t> text nodes
      const matches = [...xml.matchAll(/<a:t[^>]*>([^<]*)<\/a:t>/g)];
      const slideText = matches.map(m => m[1]).join(' ').replace(/\s+/g, ' ').trim();
      texts.push(slideText || 'This slide has no readable text.');
    }
    return texts;
  }, [loadJSZip]);

  // ── Extract text from DOCX ────────────────────────────────────────────────
  const extractDocxText = useCallback(async (url) => {
    const JSZip = await loadJSZip();
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
    const arrayBuf = await resp.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuf);
    const xml = await zip.files['word/document.xml'].async('string');
    // Extract all <w:t> text nodes
    const matches = [...xml.matchAll(/<w:t[^>]*>([^<]*)<\/w:t>/g)];
    const fullText = matches.map(m => m[1]).join(' ').replace(/\s+/g, ' ').trim();
    // Split into ~300-word pages
    const words = fullText.split(/\s+/);
    const PAGE_WORDS = 300;
    const pages = [];
    for (let i = 0; i < words.length; i += PAGE_WORDS) {
      pages.push(words.slice(i, i + PAGE_WORDS).join(' '));
    }
    return pages.length ? pages : [fullText || 'Document has no readable text.'];
  }, [loadJSZip]);

  // ── Extract text from PDF (page by page) ──────────────────────────────────
  const extractPdfText = useCallback(async (url) => {
    const pdfjsLib = await loadPdfJs();
    const pdfDoc = await pdfjsLib.getDocument({ url, withCredentials: false }).promise;
    pdfDocRef.current = pdfDoc;
    const texts = [];
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map(item => item.str).join(' ').replace(/\s+/g, ' ').trim();
      texts.push(pageText || `Page ${i} has no readable text.`);
    }
    return texts;
  }, [loadPdfJs]);

  // ── Master extractor — picks right method by file type ────────────────────
  const extractDocumentText = useCallback(async (url) => {
    if (!url) return;
    setPdfLoading(true);
    setPdfError('');
    setPdfPageTexts([]);
    try {
      let texts = [];
      if (isPdf(url)) {
        texts = await extractPdfText(url);
      } else if (isPpt(url)) {
        texts = await extractPptxText(url);
      } else if (isWord(url)) {
        texts = await extractDocxText(url);
      } else if (isExcel(url)) {
        // Excel: basic read via JSZip
        const JSZip = await loadJSZip();
        const resp = await fetch(url);
        const buf = await resp.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        const sharedXml = zip.files['xl/sharedStrings.xml']
          ? await zip.files['xl/sharedStrings.xml'].async('string')
          : '';
        const matches = [...sharedXml.matchAll(/<t[^>]*>([^<]+)<\/t>/g)];
        const text = matches.map(m => m[1]).join(' ').replace(/\s+/g, ' ').trim();
        const words = text.split(/\s+/);
        for (let i = 0; i < words.length; i += 300) {
          texts.push(words.slice(i, i + 300).join(' '));
        }
        if (!texts.length) texts = ['No readable text found in this spreadsheet.'];
      }
      setPdfPageTexts(texts);
    } catch (err) {
      console.error('[TTS] Text extraction failed:', err);
      setPdfError(`Could not read this file. ${err.message || 'Check CORS/public access.'}`);
    } finally {
      setPdfLoading(false);
    }
  }, [extractPdfText, extractPptxText, extractDocxText, loadJSZip]); // eslint-disable-line

  // Re-extract whenever the active document URL changes
  useEffect(() => {
    const url = rawUrl;
    if (!url) return;
    if (isPdf(url) || isWord(url) || isPpt(url) || isExcel(url)) {
      extractDocumentText(url);
    } else {
      setPdfPageTexts([]);
    }
  }, [rawUrl]); // eslint-disable-line

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const all = window.speechSynthesis?.getVoices() || [];
      setTtsVoices(all);
      // Default: prefer Google हिन्दी if available
      const defaultVoice = all.find(v => v.name.includes('Google') && v.lang.startsWith('hi'))
        || all.find(v => v.lang.startsWith('hi'))
        || all[0];
      if (defaultVoice && !selectedVoice) setSelectedVoice(defaultVoice.name);
    };
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
  }, []); // eslint-disable-line

  // Auto-scroll active word into view in the text panel
  useEffect(() => {
    if (activeWordRef.current && textPanelRef.current) {
      activeWordRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [ttsWordIndex]);
  useEffect(() => {
    const handler = (e) => {
      if (voiceMenuRef.current && !voiceMenuRef.current.contains(e.target)) {
        setShowVoiceMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Page timer
  useEffect(() => {
    if (ttsStatus === 'playing' || ttsStatus === 'reading') {
      ttsTimerRef.current = setInterval(() => setTtsPageTime(t => t + 1), 1000);
    } else {
      clearInterval(ttsTimerRef.current);
    }
    return () => clearInterval(ttsTimerRef.current);
  }, [ttsStatus]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const chunkRef = useRef({ chunks: [], chunkIdx: 0, page: 1, rate: 1, voice: '', pageTexts: [] });
  const pausedRef = useRef(false);

  const speakChunk = useCallback((chunks, chunkIdx, page, rate, voiceName, pageTexts) => {
    if (!window.speechSynthesis) return;
    if (chunkIdx >= chunks.length) {
      // Page done — move to next
      setTtsWordIndex(-1);
      setTtsWords([]);
      setTtsHighlight('');
      setCurrentPage(p => {
        const next = p + 1;
        if (next <= totalPages) {
          setTtsPageTime(0);
          setTimeout(() => speakPage(next, rate, voiceName, pageTexts), 400);
          return next;
        }
        setTtsStatus('stopped');
        return p;
      });
      return;
    }

    const chunk = chunks[chunkIdx];
    // Show this chunk's words in yellow box
    setTtsWords(chunk);
    setTtsHighlight(chunk.join(' '));
    setTtsWordIndex(0);

    // Save state for pause/resume
    chunkRef.current = { chunks, chunkIdx, page, rate, voice: voiceName, pageTexts };

    const text = chunk.join(' ');
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = rate;
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find(v => v.name === voiceName);
    if (voice) utter.voice = voice;

    // Track current word within chunk
    utter.onboundary = (e) => {
      if (e.name !== 'word') return;
      // Find word index by charIndex
      let count = 0;
      for (let i = 0; i < chunk.length; i++) {
        if (count >= e.charIndex) { setTtsWordIndex(i); return; }
        count += chunk[i].length + 1;
      }
      setTtsWordIndex(chunk.length - 1);
    };

    utter.onstart = () => { if (!pausedRef.current) setTtsStatus('reading'); };
    utter.onend   = () => {
      if (pausedRef.current) return; // paused — don't advance
      speakChunk(chunks, chunkIdx + 1, page, rate, voiceName, pageTexts);
    };
    utter.onerror = (e) => {
      if (e.error === 'interrupted' || e.error === 'canceled') return;
      setTtsStatus('stopped'); setTtsHighlight(''); setTtsWords([]);
    };

    ttsUtterRef.current = utter;
    window.speechSynthesis.speak(utter);
    setTtsStatus('reading');
  }, [totalPages]); // eslint-disable-line

  const speakPage = useCallback((page, rate, voiceName, pageTexts) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    pausedRef.current = false;

    const text = (pageTexts && pageTexts[page - 1])
      ? pageTexts[page - 1]
      : `Page ${page} content is not available for reading.`;

    // Split full text into words, then into 50-word chunks
    const allWords = text.split(/\s+/).filter(w => w.length > 0);
    const CHUNK_SIZE = 50;
    const chunks = [];
    for (let i = 0; i < allWords.length; i += CHUNK_SIZE) {
      chunks.push(allWords.slice(i, i + CHUNK_SIZE));
    }

    speakChunk(chunks, 0, page, rate, voiceName, pageTexts);
  }, [speakChunk]);

  const handleTtsPlay = useCallback(() => {
    if (ttsStatus === 'paused') {
      pausedRef.current = false;
      // Try native resume first; if it doesn't work, restart current chunk
      window.speechSynthesis?.resume();
      // Give browser 200ms to resume, if still paused restart chunk
      setTimeout(() => {
        if (window.speechSynthesis?.paused) {
          const { chunks, chunkIdx, page, rate, voice, pageTexts } = chunkRef.current;
          window.speechSynthesis.cancel();
          speakChunk(chunks, chunkIdx, page, rate, voice, pageTexts);
        } else {
          setTtsStatus('reading');
        }
      }, 200);
    } else {
      setTtsPageTime(0);
      speakPage(currentPage, ttsRate, selectedVoice, pdfPageTexts);
    }
  }, [ttsStatus, currentPage, ttsRate, selectedVoice, pdfPageTexts, speakPage, speakChunk]);

  const handleTtsPause = useCallback(() => {
    pausedRef.current = true;
    window.speechSynthesis?.pause();
    setTtsStatus('paused');
  }, []);

  const handleTtsStop = useCallback(() => {
    pausedRef.current = false;
    window.speechSynthesis?.cancel();
    setTtsStatus('stopped');
    setTtsHighlight('');
    setTtsWords([]);
    setTtsWordIndex(-1);
    setTtsPageTime(0);
  }, []);

  const handleTtsPrev = useCallback(() => {
    window.speechSynthesis?.cancel();
    setCurrentPage(p => {
      const prev = Math.max(1, p - 1);
      setTtsPageTime(0);
      if (ttsStatus === 'reading' || ttsStatus === 'paused') {
        setTimeout(() => speakPage(prev, ttsRate, selectedVoice, pdfPageTexts), 100);
      }
      return prev;
    });
  }, [ttsStatus, ttsRate, selectedVoice, pdfPageTexts, speakPage]);

  const handleTtsNext = useCallback(() => {
    window.speechSynthesis?.cancel();
    setCurrentPage(p => {
      const next = Math.min(totalPages, p + 1);
      setTtsPageTime(0);
      if (ttsStatus === 'reading' || ttsStatus === 'paused') {
        setTimeout(() => speakPage(next, ttsRate, selectedVoice, pdfPageTexts), 100);
      }
      return next;
    });
  }, [ttsStatus, ttsRate, selectedVoice, totalPages, pdfPageTexts, speakPage]);

  // Group voices for dropdown display
  const groupedVoices = (() => {
    const groups = {};
    ttsVoices.forEach(v => {
      const lang = v.lang.startsWith('hi') ? 'Hindi' : v.lang.startsWith('en') ? 'English' : v.lang;
      if (!groups[lang]) groups[lang] = [];
      groups[lang].push(v);
    });
    return groups;
  })();

  const SPEED_OPTIONS = [
    { label: '0.5x Slow', value: 0.5 },
    { label: '0.75x', value: 0.75 },
    { label: '1x Normal', value: 1 },
    { label: '1.25x', value: 1.25 },
    { label: '1.5x Fast', value: 1.5 },
    { label: '2x', value: 2 },
  ];

  // ── Auto-select first available content ──────────────────────────────────
  useEffect(() => {
    if (!data) return;
    const first = CONTENT_ORDER.find((k) => isValidUrl(data[k]));
    if (first) switchContent(first);
  }, [data]); // eslint-disable-line

  // ── Build strategies whenever activeContent / rawUrl changes ─────────────
  const switchContent = useCallback((key) => {
    const url = data?.[key];
    if (!isValidUrl(url)) return;
    const newStrategies = buildViewerStrategies(url, key);
    setActiveContent(key);
    setStrategies(newStrategies);
    setStrategyIdx(0);
    setAllFailed(false);
    setIframeLoading(true);
    setIframeKey((n) => n + 1);
  }, [data]);

  // ── Timeout per strategy — reads from strategy.timeout ───────────────────
  const timeoutForStrategy = (strategy) => strategy?.timeout || 30000;

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (!iframeLoading || !currentStrategy) return;

    timeoutRef.current = setTimeout(() => {
      console.warn(`[CourseViewer] Timeout on strategy: ${currentStrategy.name}`);
      tryNextStrategy();
    }, timeoutForStrategy(currentStrategy));

    return () => clearTimeout(timeoutRef.current);
  }, [iframeLoading, iframeKey]); // eslint-disable-line

  // ── Try next viewer strategy ──────────────────────────────────────────────
  const tryNextStrategy = useCallback(() => {
    setStrategyIdx((prev) => {
      const next = prev + 1;
      if (next >= strategies.length) {
        setAllFailed(true);
        setIframeLoading(false);
        return prev;
      }
      console.log(`[CourseViewer] Switching to strategy: ${strategies[next]?.name}`);
      setIframeLoading(true);
      setIframeKey((k) => k + 1);
      return next;
    });
  }, [strategies]);

  // ── iframe callbacks ──────────────────────────────────────────────────────
  const handleIframeLoad = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIframeLoading(false);
    setAllFailed(false);
    console.log(`[CourseViewer] Loaded with strategy: ${currentStrategy?.name}`);
  }, [currentStrategy]);

  const handleIframeError = useCallback(() => {
    clearTimeout(timeoutRef.current);
    console.warn(`[CourseViewer] Error on strategy: ${currentStrategy?.name}`);
    tryNextStrategy();
  }, [currentStrategy, tryNextStrategy]);

  // ── Manual retry from the beginning ──────────────────────────────────────
  const handleRetryFromStart = useCallback(() => {
    setStrategyIdx(0);
    setAllFailed(false);
    setIframeLoading(true);
    setIframeKey((k) => k + 1);
  }, []);

  const handleOpenExternal = useCallback(() => {
    if (rawUrl) window.open(rawUrl, '_blank', 'noopener,noreferrer');
  }, [rawUrl]);

  // ── Fullscreen ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.warn);
    } else {
      document.exitFullscreen().catch(console.warn);
    }
  }, []);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleBackClick = () => (onBack ? onBack() : window.history.back());
  const handlePrevPage  = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage  = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  // ── Flipbook handler ──────────────────────────────────────────────────────
  const handleFlipbookView = useCallback(async () => {
    const pdfUrl = rawUrl;
    if (!pdfUrl) return;
    // Try to get the direct PDF URL
    const cleanPdfUrl = pdfUrl.split('#')[0].split('?')[0];

    if (flipbookUrl) { setDocViewMode('flipbook'); return; }

    setFlipbookLoading(true);
    setFlipbookError('');

    try {
      const cleanUrl = pdfUrl.split('#')[0].split('?')[0];
      const pdfResponse = await fetch(cleanUrl);
      if (!pdfResponse.ok) throw new Error('PDF download failed');
      const blob = await pdfResponse.blob();

      const formData = new FormData();
      formData.append('file', blob, 'material.pdf');
      formData.append('options', JSON.stringify({ zoom: 0.75, darkMode: false, soundEnabled: true }));

      const uploadResponse = await fetch('https://dolphin-app-33jp4.ondigitalocean.app/flipbook/convert', {
        method: 'POST',
        body: formData,
      });
      if (!uploadResponse.ok) throw new Error('Flipbook API error: ' + uploadResponse.status);

      const result = await uploadResponse.json();
      if (result.success && result.data && result.data.htmlUrl) {
        setFlipbookUrl(result.data.htmlUrl);
        setDocViewMode('flipbook');
      } else {
        throw new Error(result.error || 'Flipbook API returned no URL');
      }
    } catch (e) {
      console.error('[CourseViewer] Flipbook error:', e);
      setFlipbookError(e.message || 'Flipbook conversion failed');
      setDocViewMode('pdf');
    } finally {
      setFlipbookLoading(false);
    }
  }, [rawUrl, flipbookUrl]);

  // ── Viewer badge ───────────────────────────────────────────────────────────
  const viewerBadge = currentStrategy && !iframeLoading && !allFailed ? (
    <div className="absolute bottom-4 left-4 z-10 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 border border-white/10">
      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
      <span className="text-white/70 text-xs font-medium">{currentStrategy.name}</span>
      {strategyIdx > 0 && (
        <span className="text-yellow-400 text-xs">(fallback {strategyIdx})</span>
      )}
    </div>
  ) : null;

  // ─────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="cv-viewer-root w-full h-screen flex flex-col bg-black overflow-hidden">
      <style>{`
        /* Hide all default scrollbars inside the viewer */
        .cv-viewer-root *::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .cv-viewer-root *::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.03);
          border-radius: 10px;
        }
        .cv-viewer-root *::-webkit-scrollbar-thumb {
          background: rgba(249,115,22,0.45);
          border-radius: 10px;
          transition: background 0.2s;
        }
        .cv-viewer-root *::-webkit-scrollbar-thumb:hover {
          background: rgba(249,115,22,0.75);
        }
        .cv-viewer-root *::-webkit-scrollbar-corner {
          background: transparent;
        }
        /* Firefox */
        .cv-viewer-root * {
          scrollbar-width: thin;
          scrollbar-color: rgba(249,115,22,0.45) rgba(255,255,255,0.03);
        }
      `}</style>

      {/* ── Optional Header ─────────────────────────────────────────────── */}
      {showHeader && (
        <header className="flex items-center justify-between px-4 sm:px-8 h-14 sm:h-16 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 shadow-lg flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xs sm:text-sm">🎓</span>
            </div>
            <div className="flex items-baseline gap-1.5 sm:gap-2">
              <span className="text-white font-bold text-lg sm:text-xl">ISF</span>
              <span className="text-white/90 font-semibold text-lg sm:text-xl">Athena</span>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-8 text-sm">
            {[['Standards','/standard'],['Products','/products'],['Reference','/reffrence'],
              ['Courseware','/courseware'],['Marketing Models','/marketing'],
              ['Join & Earn','/join_and_earn'],['Contact','/contact']].map(([l,h])=>(
              <a key={h} href={h} className="text-white/90 hover:text-white transition font-medium">{l}</a>
            ))}
          </nav>
          <button className="px-4 sm:px-6 py-1.5 sm:py-2 bg-white text-orange-600 rounded-md font-semibold hover:bg-orange-50 transition shadow-md text-sm">
            Get Started
          </button>
        </header>
      )}

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-900 to-black border-b border-gray-800 flex-shrink-0 gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          {/* Modules toggle button */}
          <button
            onClick={() => setShowMobileModules(v => !v)}
            className="flex items-center justify-center w-9 h-9 rounded-lg border-2 border-white/40 hover:border-orange-400 transition-all flex-shrink-0"
            title="Modules">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7"/>
            </svg>
          </button>
          <button onClick={handleBackClick}
            className="flex items-center gap-1 sm:gap-1.5 text-orange-400 hover:text-orange-300 transition-colors font-semibold text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          {data?.modal_name && (
            <span className="text-white font-semibold text-xs sm:text-sm truncate max-w-[120px] sm:max-w-xs" title={data.modal_name}>
              {data.modal_name}
            </span>
          )}
          {data?.description && (
            <span className="text-gray-400 text-xs truncate max-w-sm hidden md:inline" title={data.description}>
              {data.description}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {[{m:'pdf',l:'PDF'},{m:'doc',l:'Doc'}].map(({m,l})=>(
            <button key={m} onClick={()=>setViewMode(m)}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs font-semibold transition-all ${
                viewMode===m
                  ?'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/40'
                  :'bg-gray-800/60 text-gray-300 hover:bg-gray-700/60'
              }`}>
              {l}
            </button>
          ))}

          {/* PDF View / Flipbook View — moved to document overlay (top of content area) */}
        </div>

        {rawUrl && (
          <button onClick={handleOpenExternal}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md text-xs font-semibold transition-all flex-shrink-0"
            title="Open original file in new tab">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
            <span className="hidden sm:inline">Open</span>
          </button>
        )}
      </div>

      {/* ── Main area ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">


        {/* ── Mobile Modules Drawer (shown when hamburger is tapped) ────── */}
        {siblingsModals.length > 0 && showMobileModules && (
          <div className="lg:hidden absolute top-0 left-0 z-40 w-64 h-full bg-gray-900/98 border-r border-gray-800 flex flex-col shadow-2xl"
            style={{zIndex: 40}}>
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
              <p className="text-xs font-bold text-orange-400 uppercase tracking-wider truncate max-w-[160px]" title={productName}>{productName || 'Modules'}</p>
              <button onClick={() => setShowMobileModules(false)} className="text-gray-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {siblingsModals.map((modal, idx) => {
                const isCurrentModal = modelData &&
                  (String(modal.id) === String(modelData.id) || modal.modal_name === modelData.modal_name);
                return (
                  <button key={modal.id || idx}
                    onClick={() => { onSelectModel && onSelectModel(modal); setShowMobileModules(false); }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all border-l-2 ${
                      isCurrentModal ? 'bg-orange-500/15 border-l-orange-500' : 'border-l-transparent hover:bg-gray-800/70 hover:border-l-gray-600'
                    }`}>
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isCurrentModal ? 'bg-orange-400' : 'bg-gray-600'}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold leading-snug truncate ${isCurrentModal ? 'text-orange-300' : 'text-gray-300'}`}>
                        {modal.modal_name || modal.name || `Module ${idx + 1}`}
                      </p>
                      {modal.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{modal.description}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Left Sidebar: Same-product modals (desktop only) ──────────── */}
        {siblingsModals.length > 0 && (
          <div className="hidden lg:flex flex-col w-56 bg-gray-900/95 border-r border-gray-800 flex-shrink-0 overflow-y-auto">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
              <p className="text-xs font-bold text-orange-400 uppercase tracking-wider truncate" title={productName}>{productName || 'Modules'}</p>
            </div>
            <div className="flex-1 py-2">
              {siblingsModals.map((modal, idx) => {
                const isCurrentModal =
                  modelData &&
                  (String(modal.id) === String(modelData.id) ||
                   modal.modal_name === modelData.modal_name);
                return (
                  <button
                    key={modal.id || idx}
                    onClick={() => onSelectModel && onSelectModel(modal)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all border-l-2 ${
                      isCurrentModal
                        ? 'bg-orange-500/15 border-l-orange-500'
                        : 'border-l-transparent hover:bg-gray-800/70 hover:border-l-gray-600'
                    }`}
                  >
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                      isCurrentModal ? 'bg-orange-400' : 'bg-gray-600'
                    }`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold leading-snug truncate ${
                        isCurrentModal ? 'text-orange-300' : 'text-gray-300'
                      }`}>
                        {modal.modal_name || modal.name || `Module ${idx + 1}`}
                      </p>
                      {modal.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                          {modal.description}
                        </p>
                      )}

                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Content pane ─────────────────────────────────────────────── */}
        <div className="flex-1 bg-gray-900 relative overflow-hidden">

          {/* ── PDF View / Flipbook View — floating overlay above document ── */}
          {activeContent && contentUrls[activeContent] && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10 shadow-lg">
              <button
                onClick={() => setDocViewMode('pdf')}
                title="PDF View"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  docViewMode === 'pdf'
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/40'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}>
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                PDF View
              </button>

              <div className="w-px h-4 bg-white/20" />

              <button
                onClick={handleFlipbookView}
                disabled={flipbookLoading}
                title="Flipbook View"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  docViewMode === 'flipbook'
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-500/40'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                } ${flipbookLoading ? 'opacity-70 cursor-wait' : ''}`}>
                {flipbookLoading ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Converting...
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                    Flipbook View
                  </>
                )}
              </button>
            </div>
          )}

          {/* No data */}
          {!data && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-8">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-400">No model selected</p>
              <button onClick={handleBackClick}
                className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all text-sm">
                ← Go Back
              </button>
            </div>
          )}

          {/* No content URLs */}
          {data && !hasAnyContent && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-8">
              <div className="w-20 h-20 bg-orange-500/10 border-2 border-orange-500/30 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-orange-500/60" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-300">No content uploaded yet</p>
              <p className="text-sm text-gray-500 max-w-xs">
                No files added for <strong className="text-white">{data?.modal_name || 'this model'}</strong>
              </p>
              <button onClick={handleBackClick}
                className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all text-sm">
                ← Back to Models
              </button>
            </div>
          )}

          {/* Content area */}
          {data && hasAnyContent && (
            <>
              {/* ── LOADING OVERLAY ──────────────────────────────────────── */}
              {iframeLoading && (
                <div className="absolute inset-0 z-20 bg-gray-900 flex flex-col items-center justify-center gap-5">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-4 border-gray-700 rounded-full" />
                    <div className="absolute inset-0 border-4 border-t-orange-500 rounded-full animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-semibold text-base">
                      {currentStrategy?.label || `Loading ${CONTENT_CONFIG[activeContent]?.label || 'content'}…`}
                    </p>
                    <p className="text-orange-400 text-sm mt-1 font-medium">
                      {currentStrategy?.name}
                    </p>
                    {currentStrategy?.hint && (
                      <p className="text-green-400/80 text-xs mt-1">
                        ✓ {currentStrategy.hint}
                      </p>
                    )}
                    {strategyIdx > 0 && (
                      <p className="text-gray-500 text-xs mt-1">
                        Fallback strategy {strategyIdx} of {strategies.length - 1}
                      </p>
                    )}
                    <p className="text-gray-600 text-xs mt-2">
                      Please wait, large files take a few seconds…
                    </p>
                  </div>

                  {/* Strategy progress dots */}
                  {strategies.length > 1 && (
                    <div className="flex items-center gap-4 mt-2">
                      {strategies.map((s, i) => (
                        <div key={i} className="flex flex-col items-center gap-1.5">
                          <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            i < strategyIdx
                              ? 'bg-gray-600'
                              : i === strategyIdx
                                ? 'bg-orange-500 ring-2 ring-orange-500/40 animate-pulse scale-125'
                                : 'bg-gray-700'
                          }`} />
                          <span className={`text-xs transition-colors ${
                            i === strategyIdx ? 'text-orange-400 font-semibold' : 'text-gray-600'
                          }`}>{s.name.split(' ')[0]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── ALL STRATEGIES FAILED ────────────────────────────────── */}
              {allFailed && !iframeLoading && (
                <div className="absolute inset-0 z-20 bg-gray-900 flex flex-col items-center justify-center gap-5 px-8">
                  <div className="w-20 h-20 bg-yellow-500/15 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                  </div>

                  <div className="text-center">
                    <p className="text-white font-bold text-xl mb-2">File Cannot Be Displayed Inline</p>
                    <p className="text-gray-400 text-sm max-w-sm leading-relaxed">
                      All {strategies.length} viewer methods were attempted. This usually means the file is very large or the S3 bucket has CORS restrictions.
                    </p>
                  </div>

                  {/* Tried strategies list */}
                  <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 max-w-sm w-full">
                    <p className="text-gray-400 text-xs font-semibold mb-3 uppercase tracking-wider">Tried these viewers:</p>
                    <div className="space-y-2">
                      {strategies.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                          <span className="text-gray-400 text-sm">{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button onClick={handleRetryFromStart}
                      className="flex items-center gap-2 px-5 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-all text-sm shadow-lg shadow-orange-500/30">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      Retry All
                    </button>
                    <button onClick={handleOpenExternal}
                      className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all text-sm shadow-lg">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      Download File
                    </button>
                  </div>

                  <p className="text-gray-600 text-xs text-center max-w-xs mt-2">
                    💡 Tip: To fix this permanently, enable public CORS on your S3 bucket or upload files to Google Drive instead.
                  </p>
                </div>
              )}

              {/* ── IFRAME ───────────────────────────────────────────────── */}
              {/* Flipbook iframe */}
              {docViewMode === 'flipbook' && flipbookUrl && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'stretch', background: '#111113' }}>
                  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <iframe
                      key={'flipbook-' + flipbookUrl}
                      src={flipbookUrl}
                      title="Flipbook viewer"
                      frameBorder="0"
                      allow="autoplay; fullscreen"
                      style={{ flex: 1, width: '100%', height: '100%', border: 'none', display: 'block' }}
                    />
                  </div>
                </div>
              )}
              {/* Flipbook error */}
              {docViewMode === 'flipbook' && flipbookError && !flipbookUrl && (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <span className="text-4xl mb-3">📚</span>
                  <p className="text-red-400 font-semibold text-sm mb-2">Flipbook unavailable</p>
                  <p className="text-gray-500 text-xs">{flipbookError}</p>
                  <button onClick={() => setDocViewMode('pdf')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 transition">
                    Switch to PDF View
                  </button>
                </div>
              )}
              {/* Standard PDF/doc iframe */}
              {docViewMode === 'pdf' && currentStrategy && (
                <>
                  <style>{`
                    .cv-doc-shell {
                      position: absolute;
                      inset: 0;
                      display: flex;
                      align-items: stretch;
                      justify-content: center;
                      background: #111113;
                    }
                    .cv-doc-shell.hidden { display: none; }
                    .cv-doc-center {
                      position: relative;
                      width: 100%;
                      height: 100%;
                      display: flex;
                      flex-direction: column;
                    }
                    .cv-doc-center iframe {
                      flex: 1;
                      width: 100%;
                      height: 100%;
                      border: none;
                      display: block;
                      background: #fff;
                    }
                    /* Hide native scrollbar */
                    .cv-doc-shell::-webkit-scrollbar { display: none; }
                    .cv-doc-shell { scrollbar-width: none; -ms-overflow-style: none; }
                    .cv-doc-center::-webkit-scrollbar { width: 5px; }
                    .cv-doc-center::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); }
                    .cv-doc-center::-webkit-scrollbar-thumb { background: rgba(249,115,22,0.5); border-radius: 10px; }
                    .cv-doc-center::-webkit-scrollbar-thumb:hover { background: rgba(249,115,22,0.8); }
                  `}</style>
                  <div className={`cv-doc-shell${(iframeLoading || allFailed) ? ' hidden' : ''}`}>
                    <div className="cv-doc-center">
                      <iframe
                        key={iframeKey}
                        src={currentStrategy.url}
                        title={`${activeContent} viewer`}
                        frameBorder="0"
                        allow="autoplay; fullscreen"
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ── TTS Yellow Word Overlay — 50 words at a time ─────────── */}
              {ttsWords.length > 0 && (ttsStatus === 'reading' || ttsStatus === 'paused') && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30"
                  style={{ width: '82%', maxWidth: '720px', pointerEvents: 'none' }}>
                  <div style={{
                    background: '#FBBF24',
                    border: '3px solid #F59E0B',
                    borderRadius: '16px',
                    padding: '16px 22px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '2',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: '#111827',
                    textAlign: 'center',
                  }}>
                    {ttsWords.map((word, i) => (
                      <span key={i} style={{ display: 'inline', margin: '0 2px' }}>
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Viewer badge — bottom left */}
              {viewerBadge}
            </>
          )}
        </div>

        {/* ── Right Sidebar (desktop only) ───────────────────────────────── */}
        {showSidebar && (
          <div className="hidden lg:flex flex-col justify-center gap-3 py-8 px-4 bg-gray-900/95 border-l border-gray-800 flex-shrink-0">
            {CONTENT_ORDER.map((key) => {
              if (!contentUrls[key]) return null;
              const cfg = CONTENT_CONFIG[key];
              const isActive = activeContent === key;
              return (
                <button key={key}
                  onClick={() => switchContent(key)}
                  className={`flex flex-col items-center justify-center gap-2 transition-all duration-300 ${
                    isActive ? 'scale-110' : 'hover:scale-110'
                  }`}
                  title={cfg.label}
                >
                  <div className={`w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg transition-all ${
                    isActive
                      ? 'ring-2 ring-orange-500 shadow-orange-500/50'
                      : 'hover:shadow-orange-500/30'
                  }`}>
                    {cfg.icon}
                  </div>
                  <span className={`text-xs font-semibold transition-colors ${
                    isActive ? 'text-orange-400' : 'text-gray-400'
                  }`}>
                    {cfg.label}
                  </span>
                </button>
              );
            })}

            {/* PDF View / Flipbook View — moved to document overlay (top of content area) */}

            {/* Ask AI — commented out (coming soon) */}
            {/* <button disabled className="flex flex-col items-center gap-2 opacity-50 cursor-not-allowed" title="Ask AI (Coming Soon)">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 7H7v6h6V7z"/>
                  <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/>
                </svg>
              </div>
              <span className="text-xs font-semibold text-gray-500">Ask AI</span>
            </button> */}

            {/* Share — commented out (coming soon) */}
            {/* <button disabled className="flex flex-col items-center gap-2 opacity-50 cursor-not-allowed" title="Share (Coming Soon)">
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"/>
                </svg>
              </div>
              <span className="text-xs font-semibold text-gray-500">Share</span>
            </button> */}

            {/* Fullscreen */}
            <button onClick={toggleFullscreen}
              className="flex flex-col items-center gap-2 hover:scale-110 transition-all duration-300"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg hover:shadow-orange-500/50 transition-all">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd"/>
                </svg>
              </div>
              <span className="text-xs font-semibold text-gray-400">
                {isFullscreen ? 'Exit' : 'Fullscreen'}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile Content Switcher Bar (shown below main content on mobile/tablet) */}
      {showSidebar && (
        <div className="lg:hidden flex items-center gap-2 px-3 py-2 bg-gray-900/98 border-t border-gray-800 overflow-x-auto flex-shrink-0 scrollbar-hide">
          {CONTENT_ORDER.map((key) => {
            if (!contentUrls[key]) return null;
            const cfg = CONTENT_CONFIG[key];
            const isActive = activeContent === key;
            return (
              <button key={key}
                onClick={() => switchContent(key)}
                className={`flex flex-col items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-xl transition-all ${
                  isActive ? 'bg-orange-500/20 border border-orange-500/50' : 'border border-transparent hover:bg-gray-800'
                }`}>
                <div className={`w-9 h-9 rounded-full bg-white flex items-center justify-center shadow ${
                  isActive ? 'ring-2 ring-orange-500' : ''
                }`}>
                  <span className="scale-75">{cfg.icon}</span>
                </div>
                <span className={`text-[10px] font-semibold whitespace-nowrap ${isActive ? 'text-orange-400' : 'text-gray-400'}`}>
                  {cfg.label}
                </span>
              </button>
            );
          })}

          {/* PDF View / Flipbook View — moved to document overlay */}
          {/* Ask AI — commented out (coming soon) */}
          {/* <button disabled className="flex flex-col items-center gap-1 flex-shrink-0 px-3 py-1.5 opacity-40 cursor-not-allowed">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 7H7v6h6V7z"/>
                <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/>
              </svg>
            </div>
            <span className="text-[10px] font-semibold text-gray-500">Ask AI</span>
          </button> */}

          {/* Share — commented out (coming soon) */}
          {/* <button disabled className="flex flex-col items-center gap-1 flex-shrink-0 px-3 py-1.5 opacity-40 cursor-not-allowed">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow">
              <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"/>
              </svg>
            </div>
            <span className="text-[10px] font-semibold text-gray-500">Share</span>
          </button> */}

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} className="flex flex-col items-center gap-1 flex-shrink-0 px-3 py-1.5">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow">
              <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd"/>
              </svg>
            </div>
            <span className="text-[10px] font-semibold text-gray-400">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
          </button>
        </div>
      )}

      {/* ── TTS Bottom Bar ── always visible ────────────────────────────── */}
      <div className="relative flex-shrink-0">

        {/* ── Row 1 (mobile): status pills + page + timer ─────────────────── */}
        <div className="flex lg:hidden items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-950 border-t border-purple-800/50 overflow-x-auto scrollbar-hide flex-wrap">
          {pdfLoading && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse inline-block" />
              Extracting…
            </div>
          )}
          {pdfError && !pdfLoading && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/40 flex-shrink-0" title={pdfError}>
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
              Unavailable
            </div>
          )}
          {!pdfLoading && !pdfError && pdfPageTexts.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-teal-500/20 text-teal-300 border border-teal-500/40 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 inline-block" />
              {pdfPageTexts.length}p ready
            </div>
          )}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
            ttsStatus === 'reading' ? 'bg-green-500/20 text-green-300 border border-green-500/40'
            : ttsStatus === 'paused' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
            : 'bg-gray-700/50 text-gray-400 border border-gray-600/40'
          }`}>
            {ttsStatus === 'reading' && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />}
            {ttsStatus === 'paused' && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block" />}
            {ttsStatus === 'stopped' && <span className="w-1.5 h-1.5 rounded-full bg-gray-500 inline-block" />}
            {ttsStatus === 'reading' ? 'Reading' : ttsStatus === 'paused' ? 'Paused' : 'Stopped'}
          </div>
          <div className="ml-auto flex items-center gap-1.5 px-3 py-1 bg-purple-900/50 border border-purple-700/40 rounded-lg text-xs font-bold text-purple-200 whitespace-nowrap flex-shrink-0">
            Pg {currentPage}/{totalPages}
            <span className="text-purple-400">{formatTime(ttsPageTime)}</span>
          </div>
        </div>

        {/* ── Row 2 (mobile): Play / Pause / Stop + Voice + Speed ─────────── */}
        <div className="flex lg:hidden items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-950 border-t border-purple-800/30 overflow-x-auto scrollbar-hide">
          {/* Play */}
          <button onClick={handleTtsPlay}
            disabled={ttsStatus === 'reading' || pdfLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/></svg>
            {ttsStatus === 'paused' ? 'Resume' : 'Play'}
          </button>
          {/* Pause */}
          <button onClick={handleTtsPause}
            disabled={ttsStatus !== 'reading'}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
            Pause
          </button>
          {/* Stop */}
          <button onClick={handleTtsStop}
            disabled={ttsStatus === 'stopped'}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd"/></svg>
            Stop
          </button>
          {/* Voice selector mobile */}
          <div className="relative flex-1 min-w-[110px] max-w-[170px] flex-shrink-0" ref={voiceMenuRef}>
            <button
              onClick={() => setShowVoiceMenu(v => !v)}
              className="w-full flex items-center justify-between gap-1 px-2.5 py-1.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-lg text-xs font-medium hover:border-purple-500 transition-all">
              <span className="truncate">{selectedVoice ? selectedVoice.split(' ')[0] : 'Voice'}</span>
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </button>
            {showVoiceMenu && (
              <div className="absolute bottom-full mb-1 left-0 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-52 overflow-y-auto">
                {Object.entries(groupedVoices).map(([lang, voices]) => (
                  <div key={lang}>
                    <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                      {lang === 'Hindi' ? 'IN' : lang === 'English' ? 'US/UK' : ''} {lang}
                    </div>
                    {voices.map(v => (
                      <button key={v.name}
                        onClick={() => { setSelectedVoice(v.name); setShowVoiceMenu(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${selectedVoice === v.name ? 'bg-blue-600 text-white font-semibold' : 'text-gray-800'}`}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                ))}
                {ttsVoices.length === 0 && (
                  <div className="px-3 py-4 text-xs text-gray-500 text-center">No voices available</div>
                )}
              </div>
            )}
          </div>
          {/* Speed */}
          <select
            value={ttsRate}
            onChange={e => setTtsRate(Number(e.target.value))}
            className="px-2 py-1.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-lg text-xs font-medium hover:border-purple-500 transition-all cursor-pointer flex-shrink-0">
            {SPEED_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* ── Desktop TTS bar (single row, unchanged) ──────────────────────── */}
          <div className="hidden lg:flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-950 via-purple-950 to-indigo-950 border-t border-purple-800/50 flex-shrink-0 flex-wrap">

            {/* PDF extraction status */}
            {pdfLoading && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse inline-block" />
                Extracting text…
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/40" title={pdfError}>
                <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
                Text unavailable
              </div>
            )}
            {!pdfLoading && !pdfError && pdfPageTexts.length > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-teal-500/20 text-teal-300 border border-teal-500/40">
                <span className="w-2 h-2 rounded-full bg-teal-400 inline-block" />
                {pdfPageTexts.length}p ready
              </div>
            )}

            {/* Status pill */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold min-w-[90px] ${
              ttsStatus === 'reading' ? 'bg-green-500/20 text-green-300 border border-green-500/40'
              : ttsStatus === 'paused' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
              : 'bg-gray-700/50 text-gray-400 border border-gray-600/40'
            }`}>
              {ttsStatus === 'reading' && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />}
              {ttsStatus === 'paused' && <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />}
              {ttsStatus === 'stopped' && <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />}
              {ttsStatus === 'reading' ? 'Reading...' : ttsStatus === 'paused' ? 'Paused' : 'Stopped'}
            </div>

            {/* Play button */}
            <button onClick={handleTtsPlay}
              disabled={ttsStatus === 'reading' || pdfLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/></svg>
              {ttsStatus === 'paused' ? 'Resume' : 'Playing'}
            </button>

            {/* Pause */}
            <button onClick={handleTtsPause}
              disabled={ttsStatus !== 'reading'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
              Pause
            </button>

            {/* Stop */}
            <button onClick={handleTtsStop}
              disabled={ttsStatus === 'stopped'}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-all">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd"/></svg>
              Stop
            </button>

            {/* Voice selector */}
            <div className="relative flex-1 min-w-[140px] max-w-[220px]" ref={voiceMenuRef}>
              <button
                onClick={() => setShowVoiceMenu(v => !v)}
                className="w-full flex items-center justify-between gap-1 px-3 py-1.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-lg text-xs font-medium hover:border-purple-500 transition-all">
                <span className="truncate">{selectedVoice || 'Select Voice'}</span>
                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
              </button>
              {showVoiceMenu && (
                <div className="absolute bottom-full mb-1 left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-64 overflow-y-auto">
                  {Object.entries(groupedVoices).map(([lang, voices]) => (
                    <div key={lang}>
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                        {lang === 'Hindi' ? 'IN' : lang === 'English' ? 'US/UK' : ''} {lang}
                      </div>
                      {voices.map(v => (
                        <button key={v.name}
                          onClick={() => { setSelectedVoice(v.name); setShowVoiceMenu(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${selectedVoice === v.name ? 'bg-blue-600 text-white font-semibold' : 'text-gray-800'}`}>
                          {v.name}
                        </button>
                      ))}
                    </div>
                  ))}
                  {ttsVoices.length === 0 && (
                    <div className="px-3 py-4 text-xs text-gray-500 text-center">No voices available in your browser</div>
                  )}
                </div>
              )}
            </div>

            {/* Speed selector */}
            <select
              value={ttsRate}
              onChange={e => setTtsRate(Number(e.target.value))}
              className="px-3 py-1.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-lg text-xs font-medium hover:border-purple-500 transition-all cursor-pointer">
              {SPEED_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            {/* Page + timer */}
            <div className="ml-auto flex items-center gap-2 px-4 py-1.5 bg-purple-900/50 border border-purple-700/40 rounded-lg text-xs font-bold text-purple-200 whitespace-nowrap">
              Page {currentPage} / {totalPages}
              <span className="text-purple-400">{formatTime(ttsPageTime)}</span>
            </div>
          </div>
        </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  PROP TYPES
// ─────────────────────────────────────────────────────────────
CourseViewer.propTypes = {
  courseData: PropTypes.shape({
    bookTitle: PropTypes.string, topic: PropTypes.string,
    totalPages: PropTypes.number, category: PropTypes.string,
    ppt: PropTypes.string, document: PropTypes.string,
    book: PropTypes.string, reference_book: PropTypes.string,
    website: PropTypes.string, social_media: PropTypes.string,
  }),
  modelData: PropTypes.shape({
    modal_name: PropTypes.string, description: PropTypes.string,
    ppt: PropTypes.string, document: PropTypes.string,
    book: PropTypes.string, reference_book: PropTypes.string,
    website: PropTypes.string, social_media: PropTypes.string,
  }),
  onBack: PropTypes.func,
  siblingsModals: PropTypes.array,
  onSelectModel: PropTypes.func,
  productName: PropTypes.string,
  showSidebar: PropTypes.bool,
  showBottomControls: PropTypes.bool,
  showHeader: PropTypes.bool,
};

export default CourseViewer;